<template>
  <div class="pa-4">
    <breadcrumb />

    <div class="flex-wrap mb-4 d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <h2 class="mr-3 black--text">{{ $t("reports.anomalyDetection.title") }}</h2>
        <v-btn small rounded color="primary" depressed @click="showGuide = true">
          <v-icon left small>mdi-help-circle-outline</v-icon>
          {{ $t("reports.anomalyDetection.aiReportGuide") }}
        </v-btn>
      </div>
      <div>
        <v-btn outlined color="primary" class="mr-2" @click="goBack">{{ $t("reports.anomalyDetection.back") }}</v-btn>
        <v-btn outlined color="red" class="mr-2" @click="deleteDialog = true">{{ $t("reports.anomalyDetection.deleteReport") }}</v-btn>
        <v-btn color="primary" depressed :disabled="!isDownloadable" :loading="downloading" @click="download">
          {{ $t("reports.anomalyDetection.downloadReport") }}
        </v-btn>
      </div>
    </div>

    <v-card class="pa-4" outlined v-if="report">
      <!-- Meta -->
      <h3 class="mb-3 black--text">{{ report.farmName ? report.farmName + " Report" : $t("reports.anomalyDetection.title") }}</h3>
      <v-row class="mb-1">
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.dateOfReport") }}</div>
          <div class="font-weight-bold">{{ report.createdAt | formatDate }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.farm") }}</div>
          <div class="font-weight-bold">{{ report.farmName || "-" }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.zone") }}</div>
          <div class="font-weight-bold">{{ report.zoneName || "-" }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.location") }}</div>
          <div class="font-weight-bold">{{ report.locationName || "-" }}</div>
          <div v-if="report.centerLatitude != null" class="grey--text text-caption">{{ coordLabel }}</div>
        </v-col>
      </v-row>
      <v-row class="mb-1">
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.startDate") }}</div>
          <div class="font-weight-bold">{{ report.selectedDate | formatDate }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.numberOfHistoricalReports") }}</div>
          <div class="font-weight-bold">{{ report.historicalReports }}</div>
        </v-col>
      </v-row>

      <v-btn color="primary" depressed class="my-3" @click="goCompare">
        {{ $t("reports.anomalyDetection.compareReports") }}
      </v-btn>

      <!-- Map + status overlay -->
      <div class="map-holder">
        <div v-if="isGenerating" class="gen-overlay">
          <v-progress-circular indeterminate color="primary" size="42" />
          <div class="mt-3 black--text">{{ $t("reports.anomalyDetection.generating") }}</div>
          <div class="grey--text text-caption">{{ $t("reports.anomalyDetection.generatingHelp") }}</div>
        </div>
        <v-btn v-if="dates.length" icon class="nav-arrow left" @click="stepDate(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
        <v-btn v-if="dates.length" icon class="nav-arrow right" @click="stepDate(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
        <index-overlay-map
          :overlays="overlays"
          :outline-coordinates="report.coordinates || []"
          :circle="circleOutline"
          height="460px"
        />
      </div>

      <p v-if="closestNote" class="mt-2 mb-0 grey--text text--darken-1 font-italic">{{ closestNote }}</p>

      <v-alert v-if="report.status === 'PARTIAL'" type="warning" dense text class="mt-2">
        {{ $t("reports.anomalyDetection.partialWarning") }}
      </v-alert>
      <v-alert v-if="report.status === 'FAILED'" type="error" dense text class="mt-2">
        {{ $t("reports.anomalyDetection.failed") }}
      </v-alert>

      <!-- Available-date strip: selecting a new date generates a NEW report -->
      <div class="mt-3 date-strip" v-if="dates.length">
        <span class="mr-3 black--text font-weight-medium">{{ $t("reports.anomalyDetection.selectDate") }}</span>
        <v-slide-group
          ref="dateSlide"
          v-model="activeDate"
          center-active
          show-arrows
          class="date-slide"
          @wheel.native.prevent="onDateWheel"
          @mousedown.native="onDateDragStart"
        >
          <v-slide-item v-for="d in dates" :key="d" :value="d">
            <v-btn
              text
              small
              class="mx-1"
              :class="{ 'active-date': d === report.selectedDate }"
              @click="onDateClick(d)"
            >
              {{ d | formatDate }}
            </v-btn>
          </v-slide-item>
        </v-slide-group>
      </div>

      <v-divider class="my-4" />

      <!-- Report details (performance bands) -->
      <h4 class="mb-3 primary--text">{{ $t("reports.anomalyDetection.reportDetails") }}</h4>
      <anomaly-bands :bands="primaryBands" row />
      <anomaly-colorbar class="mt-4" />

      <v-alert type="warning" text class="mt-4" border="left" icon="mdi-alert">
        <strong>{{ $t("reports.anomalyDetection.disclaimerTitle") }}</strong><br />
        {{ $t("reports.anomalyDetection.disclaimer") }}
      </v-alert>

      <v-divider class="my-4" />

      <!-- Feedback -->
      <feedback-display
        :feedback="report.feedback"
        i18n-prefix="reports.anomalyDetection"
        @add="onAddFeedback"
      />
    </v-card>

    <feedback-dialog
      :value="showFeedback"
      :report-uid="reportUid"
      :service="feedbackService"
      :default-agree="feedbackDefaultAgree"
      i18n-prefix="reports.anomalyDetection"
      upload-folder="anomaly-detection/feedback"
      @input="showFeedback = $event"
      @submitted="onFeedbackSubmitted"
    />
    <guide-dialog :value="showGuide" @input="showGuide = $event" />

    <confirm-box
      v-if="deleteDialog"
      :dialog="deleteDialog"
      :title="$t('reports.anomalyDetection.deleteConfirmTitle')"
      :message="$t('reports.anomalyDetection.deleteConfirmMessage')"
      :confirm-btn-text="$t('reports.anomalyDetection.delete')"
      :cancel-btn-text="$t('reports.anomalyDetection.cancel')"
      @continue:action="doDelete"
      @cancel:action="deleteDialog = false"
    />
    <confirm-box
      v-if="generatePrompt"
      :dialog="generatePrompt"
      :title="$t('reports.anomalyDetection.selectDate')"
      :message="$t('reports.anomalyDetection.notGeneratedPrompt')"
      :confirm-btn-text="$t('reports.anomalyDetection.generateNow')"
      :cancel-btn-text="$t('reports.anomalyDetection.cancel')"
      @continue:action="doGenerateForDate"
      @cancel:action="generatePrompt = false"
    />
  </div>
</template>

<script>
import AnomalyDetectionService from "@/_services/AnomalyDetectionService";
// Reuse the read-only raster-overlay map from Crop Progression.
import IndexOverlayMap from "../crop-progression/components/IndexOverlayMap.vue";
import AnomalyBands from "./components/AnomalyBands.vue";
import AnomalyColorbar from "./components/AnomalyColorbar.vue";
import FeedbackDialog from "../shared/FeedbackDialog.vue";
import FeedbackDisplay from "../shared/FeedbackDisplay.vue";
import GuideDialog from "./components/GuideDialog.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";
import loadingMixin from "@/mixins/LoadingMixin";
import downloadMixin from "@/mixins/DownloadMixin";
import reportError from '../../../mixins/reportError.js';

const POLL_MS = 12000;

export default {
  name: "AnomalyDetectionView",
  components: { IndexOverlayMap, AnomalyBands, AnomalyColorbar, FeedbackDialog, FeedbackDisplay, GuideDialog, ConfirmBox },
  mixins: [loadingMixin, downloadMixin, reportError],
  data() {
    return {
      report: null,
      dates: [],
      generatedMap: {},
      showFeedback: false,
      feedbackDefaultAgree: null,
      showGuide: false,
      deleteDialog: false,
      generatePrompt: false,
      pendingDate: null,
      downloading: false,
      pollTimer: null,
      dragStartX: 0,
      dragStartOffset: 0,
      dragMoved: false,
    };
  },
  computed: {
    feedbackService() {
      return AnomalyDetectionService;
    },
    reportUid() {
      return this.$route.params.reportUid;
    },
    isGenerating() {
      return this.report && this.report.status === "GENERATING";
    },
    isDownloadable() {
      return this.report && ["COMPLETED", "PARTIAL"].includes(this.report.status);
    },
    primaryBands() {
      return (this.report && this.report.bands) || { green: null, yellow: null, red: null };
    },
    overlays() {
      if (this.report && this.report.imageUrl && this.report.bbox) {
        return [{ url: this.report.imageUrl, bbox: this.report.bbox, opacity: 1, clipRightPct: null }];
      }
      return [];
    },
    circleOutline() {
      if (this.report && this.report.radius) {
        return {
          lat: this.report.centerLatitude,
          lng: this.report.centerLongitude,
          radiusKm: this.report.radius,
        };
      }
      return null;
    },
    coordLabel() {
      if (!this.report || this.report.centerLatitude == null) return "";
      return `${Number(this.report.centerLatitude).toFixed(6)}, ${Number(this.report.centerLongitude).toFixed(6)}`;
    },
    // Drives the slide-group's active item so the selected date scrolls into view
    // on load (center-active). Navigation happens via the button click, so the
    // setter is a no-op.
    activeDate: {
      get() {
        return this.report ? this.report.selectedDate : null;
      },
      set() {},
    },
    // Note shown when the exact requested date's imagery was unavailable.
    closestNote() {
      if (!this.report || !this.report.resolvedDate) return "";
      if (this.report.resolvedDate === this.report.selectedDate) return "";
      return this.$t("reports.anomalyDetection.closestImageNote", {
        requested: this.$options.filters.formatDate(this.report.selectedDate),
        resolved: this.$options.filters.formatDate(this.report.resolvedDate),
      });
    },
  },
  watch: {
    "$route.params.reportUid"() {
      this.reload();
    },
  },
  created() {
    this.reload();
  },
  beforeDestroy() {
    this.clearPoll();
    window.removeEventListener("mousemove", this.onDateDrag);
    window.removeEventListener("mouseup", this.onDateDragEnd);
  },
  methods: {
    clearPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async reload() {
      this.clearPoll();
      this.startLoading();
      try {
        await this.fetchReport();
        await this.fetchRelatedDates();
        if (this.isGenerating) this.startPolling();
      } finally {
        this.stopLoading();
      }
    },
    async fetchReport() {
      try {
        const env = await AnomalyDetectionService.getReport(this.reportUid);
        this.report = (env && env.data) || null;
      } catch (e) {
        this.$notify({ text: this.$t("reports.anomalyDetection.loadFailed"), type: "error" });
      }
    },
    startPolling() {
      this.clearPoll();
      this.pollTimer = setInterval(async () => {
        await this.fetchReport();
        if (!this.isGenerating) this.clearPoll();
      }, POLL_MS);
    },
    async fetchRelatedDates() {
      try {
        const env = await AnomalyDetectionService.getRelatedDates(this.reportUid);
        const d = (env && env.data) || {};
        this.dates = d.dates || [];
        this.generatedMap = d.generated || {};
      } catch (e) {
        this.dates = [];
      }
    },
    // Mouse wheel + drag scrolling for the date strip. Drives the slide-group's
    // own `scrollOffset` (the same state its arrows use), so all three stay in sync.
    dateSlideEls() {
      const sg = this.$refs.dateSlide;
      if (!sg || !sg.$el) return null;
      return {
        sg,
        wrapper: sg.$el.querySelector(".v-slide-group__wrapper"),
        content: sg.$el.querySelector(".v-slide-group__content"),
      };
    },
    scrollDateBy(delta) {
      const els = this.dateSlideEls();
      if (!els || !els.wrapper || !els.content) return;
      const max = Math.max(0, els.content.scrollWidth - els.wrapper.clientWidth);
      const cur = Number(els.sg.scrollOffset) || 0;
      els.sg.scrollOffset = Math.min(Math.max(cur + delta, 0), max);
    },
    onDateWheel(e) {
      this.scrollDateBy(e.deltaY !== 0 ? e.deltaY : e.deltaX);
    },
    onDateDragStart(e) {
      const els = this.dateSlideEls();
      if (!els) return;
      this.dragStartX = e.clientX;
      this.dragStartOffset = Number(els.sg.scrollOffset) || 0;
      this.dragMoved = false;
      window.addEventListener("mousemove", this.onDateDrag);
      window.addEventListener("mouseup", this.onDateDragEnd);
    },
    onDateDrag(e) {
      const els = this.dateSlideEls();
      if (!els || !els.wrapper || !els.content) return;
      const dx = e.clientX - this.dragStartX;
      if (Math.abs(dx) > 3) this.dragMoved = true;
      const max = Math.max(0, els.content.scrollWidth - els.wrapper.clientWidth);
      els.sg.scrollOffset = Math.min(Math.max(this.dragStartOffset - dx, 0), max);
    },
    onDateDragEnd() {
      window.removeEventListener("mousemove", this.onDateDrag);
      window.removeEventListener("mouseup", this.onDateDragEnd);
      // Keep dragMoved set through the click that follows mouseup, then clear it.
      this.$nextTick(() => {
        this.dragMoved = false;
      });
    },
    stepDate(dir) {
      if (!this.dates.length) return;
      const i = this.dates.indexOf(this.report.selectedDate);
      const next = this.dates[i + dir];
      if (next) this.onDateClick(next);
    },
    onDateClick(date) {
      // Ignore the click that ends a drag-scroll.
      if (this.dragMoved) return;
      if (date === this.report.selectedDate) return;
      const gen = this.generatedMap[date];
      if (gen && gen.reportUid) {
        this.$router.push({ name: "anomalyDetectionView", params: { reportUid: gen.reportUid } });
        return;
      }
      this.pendingDate = date;
      this.generatePrompt = true;
    },
    async doGenerateForDate() {
      this.generatePrompt = false;
      if (!this.pendingDate) return;
      this.startLoading();
      try {
        const env = await AnomalyDetectionService.compareReports({
          reportUid: this.reportUid,
          dates: [this.pendingDate],
        });
        const reports = (env && env.data && env.data.reports) || [];
        if (reports[0] && reports[0].reportUid) {
          this.$router.push({ name: "anomalyDetectionView", params: { reportUid: reports[0].reportUid } });
        } else {
          this.$notify({
            text: this.reportErrorMessage({ data: env }, 'reports.anomalyDetection.generateFailed'),
            type: 'error',
          });
        }
      } catch (e) {
        this.$notify({
          text: this.reportErrorMessage(e, 'reports.anomalyDetection.generateFailed'),
          type: 'error',
        });
      } finally {
        this.pendingDate = null;
        this.stopLoading();
      }
    },
    async download() {
      this.downloading = true;
      try {
        const res = await AnomalyDetectionService.downloadPdf(this.reportUid);
        this.downloadBlob(res.data, `anomaly_detection_${this.reportUid}.pdf`);
      } catch (e) {
        this.$notify({ text: this.$t("reports.anomalyDetection.loadFailed"), type: "error" });
      } finally {
        this.downloading = false;
      }
    },
    onAddFeedback(agree) {
      this.feedbackDefaultAgree = agree;
      this.showFeedback = true;
    },
    onFeedbackSubmitted() {
      this.showFeedback = false;
      this.$notify({ title: this.$t("success"), text: this.$t("reports.anomalyDetection.feedbackSubmitted"), type: "success" });
      this.fetchReport();
    },
    async doDelete() {
      this.deleteDialog = false;
      this.startLoading();
      try {
        await AnomalyDetectionService.deleteReport(this.reportUid);
        this.$notify({ title: this.$t("success"), text: this.$t("reports.anomalyDetection.deleted"), type: "success" });
        this.$router.push({ name: "anomalyDetectionList" });
      } catch (e) {
        this.$notify({ text: this.$t("reports.anomalyDetection.loadFailed"), type: "error" });
      } finally {
        this.stopLoading();
      }
    },
    goCompare() {
      this.$router.push({ name: "anomalyDetectionCompare", params: { reportUid: this.reportUid } });
    },
    goBack() {
      this.$router.push({ name: "anomalyDetectionList" });
    },
  },
};
</script>

<style scoped>
.map-holder {
  position: relative;
}
.gen-overlay {
  position: absolute;
  z-index: 5;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}
.nav-arrow {
  position: absolute;
  z-index: 4;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
}
.nav-arrow.left {
  left: 8px;
}
.nav-arrow.right {
  right: 8px;
}
.date-strip {
  display: flex;
  align-items: center;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 6px 10px;
  overflow: hidden;
}
/* Let the slide-group take the remaining width and scroll internally instead of
   overflowing the card (a flex child defaults to min-width:auto). */
.date-strip ::v-deep .v-slide-group {
  min-width: 0;
  flex: 1 1 auto;
}
.date-strip ::v-deep .v-slide-group__content {
  flex-wrap: nowrap;
}
.date-strip ::v-deep .v-slide-group__wrapper {
  cursor: grab;
}
.date-strip ::v-deep .v-slide-group__wrapper:active {
  cursor: grabbing;
}
.active-date {
  color: #159957 !important;
  border-bottom: 2px solid #159957;
  font-weight: bold;
}
</style>
